import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
} from '@mui/material';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useUser } from '@clerk/clerk-react';
import { useApi } from '../../hooks/useApi';
import teamsLogo from '../../assets/teams-logo.png';

interface IntegrationStatusProps {
  agentId: string | number;
  onConnectionChange?: (connected: boolean) => void;
}

type Status = 'loading' | 'disconnected' | 'pending' | 'connected' | 'error';

export const IntegrationStatus = ({ agentId, onConnectionChange }: IntegrationStatusProps) => {
  const { user } = useUser();
  const { apiFetch } = useApi();
  const [status, setStatus] = useState<Status>('loading');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.id) {
      checkStatus();
    }
  }, [agentId, user?.id]);

  const checkStatus = async () => {
    setStatus('loading');
    setError(null);
    
    try {
      const res = await apiFetch(`integrations/${agentId}/teams/status`);
      
      if (!res.ok) {
        throw new Error('Failed to check status');
      }
      
      const data = await res.json();
      const isConnected = data.connected;
      setStatus(isConnected ? 'connected' : (data.status || 'disconnected'));
      onConnectionChange?.(isConnected);
    } catch (err) {
      console.error('Error checking Teams status:', err);
      setStatus('disconnected');
      onConnectionChange?.(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    setError(null);
    
    try {
      const res = await apiFetch(`integrations/${agentId}/teams/connect`, {
        method: 'POST',
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to initiate connection');
      }

      const data = await res.json();

      if (data.redirectUrl) {
        // Open OAuth in popup window instead of replacing current page
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        
        const popup = window.open(
          data.redirectUrl,
          'teams-oauth',
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
        );

        // Poll for completion
        const pollInterval = setInterval(async () => {
          try {
            // Check if popup was closed
            if (popup?.closed) {
              clearInterval(pollInterval);
              // Check final status
              const statusRes = await apiFetch(`integrations/${agentId}/teams/status`);
              const statusData = await statusRes.json();
              
              if (statusData.connected) {
                setStatus('connected');
              } else {
                setStatus('disconnected');
              }
              setConnecting(false);
              return;
            }

            // Check status while popup is open
            const statusRes = await apiFetch(`integrations/${agentId}/teams/status`);
            const statusData = await statusRes.json();
            
            if (statusData.connected) {
              clearInterval(pollInterval);
              popup?.close();
              setStatus('connected');
              setConnecting(false);
              onConnectionChange?.(true);
            }
          } catch (e) {
            console.error('Error polling status:', e);
          }
        }, 2000); // Poll every 2 seconds

        // Timeout after 5 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          if (connecting) {
            setConnecting(false);
            setError('Connection timed out');
          }
        }, 300000);
      } else {
        throw new Error('No redirect URL received');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Connection failed';
      console.error('Error connecting Teams:', err);
      setError(errorMessage);
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect Microsoft Teams from this agent?')) {
      return;
    }

    try {
      const res = await apiFetch(`integrations/${agentId}/teams`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        throw new Error('Failed to disconnect');
      }

      setStatus('disconnected');
      onConnectionChange?.(false);
    } catch (err) {
      console.error('Error disconnecting Teams:', err);
      setError('Failed to disconnect');
    }
  };

  if (status === 'loading') {
    return <CircularProgress size={16} />;
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Tooltip title="Microsoft Teams">
        <Box 
          sx={{ 
            opacity: status === 'connected' ? 1 : 0.4,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <img 
            src={teamsLogo} 
            alt="Microsoft Teams" 
            style={{ width: 24, height: 24, objectFit: 'contain' }} 
          />
        </Box>
      </Tooltip>

      {status === 'connected' ? (
        <>
          <Chip 
            label="Connected" 
            color="success" 
            size="small" 
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
          <Tooltip title="Disconnect Teams">
            <IconButton size="small" onClick={handleDisconnect} color="error">
              <LinkOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <Button
          size="small"
          variant="outlined"
          onClick={handleConnect}
          disabled={connecting}
          sx={{ minWidth: 80, fontSize: '0.75rem' }}
        >
          {connecting ? 'Connecting...' : 'Connect'}
        </Button>
      )}

      {error && (
        <Tooltip title={error}>
          <Chip label="Error" color="error" size="small" sx={{ height: 20 }} />
        </Tooltip>
      )}
    </Stack>
  );
};

export default IntegrationStatus;
